/**
 * IconAction — a row action drawn as an icon, with a hover / focus card saying
 * what it does.
 *
 * Jesse, 2026-10-30: the Construction Deadlines rows carried five text buttons,
 * which pushed the table off the right of the screen, and he wanted a hover that
 * explains each action in full. Icons are what the Leads page already uses for
 * row actions; this adds the explanation.
 *
 * Why not a plain `title`: it shows only the short name, only to a mouse, after
 * the browser's own long delay. This card shows the name and a sentence, opens
 * after a short rest of the pointer or at once on keyboard focus, and is wired
 * to the button with aria-describedby so a screen reader reads it too. The
 * button's accessible name is `label`, so touch and screen-reader users still
 * know what they are pressing.
 *
 * The card is position: fixed, placed from the button's on-screen box. Rows live
 * inside a horizontally scrolling table, which would clip an absolutely
 * positioned card at its edge.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

const HOVER_DELAY_MS = 300
const CARD_WIDTH = 256
const GUTTER = 8

type Tone = 'default' | 'primary' | 'danger'

const TONE: Record<Tone, string> = {
  default: 'text-gray-500 hover:text-gray-800 hover:bg-gray-100',
  primary: 'text-primary-600 hover:text-primary-800 hover:bg-primary-50',
  danger: 'text-red-500 hover:text-red-700 hover:bg-red-50',
}

interface IconActionProps {
  /** Short name: the button's accessible name and the card's first line. */
  label: string
  /** What it does, in a sentence or two. */
  description: React.ReactNode
  icon: React.ReactNode
  tone?: Tone
  disabled?: boolean
  onClick?: () => void
  /** In-app route. */
  to?: string
  /** Opens in a new tab (e.g. a printable page). */
  href?: string
}

export default function IconAction({
  label, description, icon, tone = 'default', disabled, onClick, to, href,
}: IconActionProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const anchor = useRef<HTMLElement | null>(null)
  const timer = useRef<number | null>(null)
  const cardId = useId()

  function cancel() {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
  }

  function show() {
    const el = anchor.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const viewport = document.documentElement.clientWidth
    // Right-aligned to the button (actions sit at the right of a row), kept on screen.
    const left = Math.max(GUTTER, Math.min(r.right - CARD_WIDTH, viewport - CARD_WIDTH - GUTTER))
    setPos({ top: r.bottom + 6, left })
  }

  function hide() {
    cancel()
    setPos(null)
  }

  useEffect(() => cancel, [])

  // A fixed card would float away from its button on scroll; close it instead.
  useEffect(() => {
    if (!pos) return
    // setPos directly: a pending hover timer cannot exist while the card shows.
    const close = () => setPos(null)
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    document.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [pos])

  const handlers = {
    onPointerEnter: (e: React.PointerEvent) => {
      if (e.pointerType === 'touch') return
      cancel()
      timer.current = window.setTimeout(show, HOVER_DELAY_MS)
    },
    onPointerLeave: hide,
    onFocus: (e: React.FocusEvent) => {
      // Keyboard focus only; a mouse click focuses too and would pop the card.
      if ((e.target as HTMLElement).matches(':focus-visible')) show()
    },
    onBlur: hide,
    'aria-label': label,
    'aria-describedby': pos ? cardId : undefined,
    className: `inline-flex h-8 w-8 items-center justify-center rounded outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-40 disabled:pointer-events-none ${TONE[tone]}`,
  }

  let control: React.ReactNode
  if (href) {
    control = (
      <a ref={el => { anchor.current = el }} href={href} target="_blank" rel="noopener noreferrer" {...handlers}>
        {icon}
      </a>
    )
  } else if (to) {
    control = <Link ref={el => { anchor.current = el }} to={to} {...handlers}>{icon}</Link>
  } else {
    control = (
      <button
        ref={el => { anchor.current = el }}
        type="button"
        disabled={disabled}
        onClick={() => { hide(); onClick?.() }}
        {...handlers}
      >
        {icon}
      </button>
    )
  }

  return (
    <>
      {control}
      {pos && (
        <span
          id={cardId}
          role="tooltip"
          style={{ top: pos.top, left: pos.left, width: CARD_WIDTH }}
          className="fixed z-40 rounded-md border border-gray-200 bg-white p-3 text-left text-xs font-normal leading-relaxed text-gray-600 shadow-lg whitespace-normal pointer-events-none"
        >
          <span className="block font-semibold text-gray-900 mb-0.5">{label}</span>
          {description}
        </span>
      )}
    </>
  )
}

/** A row's actions, laid out the same way everywhere. */
export function IconActions({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-end gap-0.5">{children}</div>
}
